<i18n>
{
	"en": {
		"addToTrip": "Add to Trip",
		"addToTransporter": "External Transporter",
		"moveOrder": "Move Order to Different Trip",
		"removeOrderFromTripAndMerge": "Remove from trip",
		"removeOrderFromTrip": "Remove from trip, but keep stretch",
		"failedToRemoveOrderTrip": "Failed to remove order from trip",
		"failedToRemoveOrderTransporter": "Failed to remove order from transporter",
		"orderRemovedFromTrip": "Order removed from trip",
		"orderRemovedFromTransporter": "Order removed from transporter",
		"orderAddedToTrip": "Order added to trip {number}",
		"orderAddedToTransporter": "Order added to transporter",
		"orderAlreadyAdded": "A selected order is already part of the selected trip.",
		"validationErrors": "Trip is currently invalid, please edit trip before adding orders.",
		"failedToAddOrderToTrip": "Failed to add order to trip {number}",
		"failedToAddOrderToTransporter": "Failed to add order to transporter",
		"unableToUpdateTrip": "Could not update trip",
		"addSubOrderBefore": "Add Stretch Before",
		"removeSubOrder": "Remove Stretch",
		"subOrderDeleted": "Stretch removed",
		"failedToDeleteSubOrder": "Failed to remove stretch",
		"subOrderAdded": "Stretch added",
		"failedToAddSubOrderBefore": "Failed to add stretch"
	},
	"no": {
		"addToTrip": "Legg på tur",
		"addToTransporter": "Ekstern leverandør",
		"moveOrder": "Flytt oppdrag til annen tur",
		"removeOrderFromTripAndMerge": "Fjern fra tur",
		"removeOrderFromTrip": "Fjern fra tur og behold strekning",
		"failedToRemoveOrderTrip": "Kunne ikke fjerne ordren fra turen",
		"failedToRemoveOrderTransporter": "Kunne ikke fjerne oppdrag fra transportøren",
		"orderRemovedFromTrip": "Oppdrag fjernet fra turen",
		"orderRemovedFromTransporter": "Oppdrag fjernet fra transportøren",
		"orderAddedToTrip": "Oppdrag lagt til tur {number}",
		"orderAddedToTransporter": "Oppdrag lagt til transportøren",
		"orderAlreadyAdded": "Et valgt oppdrag er allerede en del av den valgte turen.",
		"validationErrors": "Turen er ugyldig. Rediger turen før du legger til oppdrag.",
		"failedToAddOrderToTrip": "Kunne ikke legge til oppdrag på turen {number}",
		"failedToAddOrderToTransporter": "Kunne ikke legge til oppdrag til transportøren",
		"unableToUpdateTrip": "Kunne ikke oppdatere tur",
		"addSubOrderBefore": "Lag ny strekning foran",
		"removeSubOrder": "Fjern strekning",
		"subOrderDeleted": "Strekning fjernet",
		"failedToDeleteSubOrder": "Kunne ikke fjerne strekning",
		"subOrderAdded": "Strekning lagt til",
		"failedToAddSubOrderBefore": "Kunne ikke legge til strekning"
	}
}
</i18n>

<template>
	<span>
		<t-tab
			v-for="subOrderTab in subOrderTabs"
			:key="subOrderTab.index"
			:value="subOrderTab.name"
			:selected="subOrderTab.index === currentOrderTabIndex"
			disableTabbing
			:isInvalid="errors.subOrders[subOrderTab.id] && errors.subOrders[subOrderTab.id].isInvalid"
			@selected="$emit('selectSubOrder',subOrderTab.index)"
		>
			<t-status-color-dot :color="getStatusColor(subOrders[subOrderTab.index].status)" style="margin-right: 5px" />
			{{ subOrderTab.name }}
			<template v-slot:contextMenu>
				<t-menu>
					<t-menu-group>
						<t-menu-item
							icon="plus"
							@click="showAddSubOrderBefore(subOrderTab.index)"
						>
							<strong>{{ $t('addSubOrderBefore') }}</strong>
						</t-menu-item>
												
						<template v-if="!subOrderTab.tripNumber && !subOrderTab.external">
							<t-menu-item
								v-if="subOrderTab.prev !== 0 || subOrderTab.next !== 0"
								icon="minus"
								@click="onDeleteSubOrder(subOrderTab.index)"
							>
								<strong>{{ $t('removeSubOrder') }}</strong>
							</t-menu-item>
							<hr>
							<t-menu-item
								icon="inheritance"
								@click="showAddToTrip(subOrderTab.index)"
							>
								{{ $t('addToTrip') }}
							</t-menu-item>
							<t-menu-item
								icon="inheritance"
								@click="showAddToTransporter(subOrderTab.index)"
							>
								{{ $t('addToTransporter') }}
							</t-menu-item>
						</template>
						<hr>
						<t-menu-item
							v-if="subOrderTab.tripNumber"
							icon="inheritance"
							@click="showMoveLoadedOrder(subOrderTab.index)"
						>
							{{ $t('moveOrder') }}
						</t-menu-item>
					
						<template v-if="subOrderTab.tripNumber || subOrderTab.external">
							<hr>
							<t-menu-item icon="cross" @click="onRemoveOrder(subOrderTab.index, true)">
								{{ $t('removeOrderFromTripAndMerge') }}
							</t-menu-item>
							<t-menu-item icon="cross" @click="onRemoveOrder(subOrderTab.index)">
								{{ $t('removeOrderFromTrip') }}
							</t-menu-item>
						</template>
					</t-menu-group>
				</t-menu>
			</template>
		</t-tab>

		<AddSubOrderBefore
			:isShown="addSubOrderBeforeVisible"
			:order="processingSubOrder"
			@close="addSubOrderBeforeVisible = false"
			@addSubOrderBefore="onAddSubOrderBefore"
		/>

		<ConfirmDeleteSubOrder
			:isShown="confirmDeleteSubOrderVisible"
			@close="confirmDeleteSubOrderVisible = false"
			@confirm="confirmDeleteSubOrder"
		/>

		<AddOrderToTripSelect
			:isShown="addToTripVisible"
			:order="processingSubOrder"
			:disableSubmit="addToTripProcessing"

			@close="addToTripVisible = false"
			@addToTrip="onAddOrderToTrip"
		/>
		<AddOrderToTransporterSelect
			:isShown="addToTransporterVisible"
			:order="processingSubOrder"
			:disableSubmit="addToTransporterProcessing"

			@close="addToTransporterVisible = false"
			@addToTransporter="onAddOrderToTransporter"
		/>

		<ConfirmRemoveTripOrder
			:isShown="confirmRemoveTripOrderVisible"
			:subOrder="processingSubOrder"
			@close="confirmRemoveTripOrderVisible = false"
			@confirm="confirmRemoveOrderFromTrip"
		/>
		<ConfirmRemoveTransporterOrder
			:isShown="confirmRemoveTransporterOrderVisible"
			@close="confirmRemoveTransporterOrderVisible = false"
			@confirm="confirmRemoveOrderFromTransporter"
		/>
		<MoveOrderToDifferentTripSelect
			:isShown="moveLoadedOrderVisible"
			:order="processingSubOrder"
			:currentTrip="subOrderTrip"
			:disableSubmit="moveLoadedOrderProcessing"
			@close="moveLoadedOrderVisible = false"
			@moveOrder="onMoveOrderToDifferentTrip"
		/>
	</span>
</template>

<script>
import { statuses } from '@/core';
import { mapState, mapActions } from './store/newOrder.store.js';
import TStatusColorDot from '@/components/evergreen/badges/TStatusColorDot';
import AddSubOrderBefore from '@/modules/newOrder/modals/subOrderMenu/AddSubOrderBefore';
import ConfirmDeleteSubOrder from '@/modules/newOrder/modals/subOrderMenu/ConfirmDeleteSubOrder';
import AddOrderToTripSelect from '@/modules/newOrder/modals/subOrderMenu/AddOrderToTripSelect';
import AddOrderToTransporterSelect from '@/modules/newOrder/modals/subOrderMenu/AddOrderToTransporterSelect';
import ConfirmRemoveTripOrder from '@/modules/newOrder/modals/subOrderMenu/ConfirmRemoveTripOrder';
import ConfirmRemoveTransporterOrder from '@/modules/newOrder/modals/subOrderMenu/ConfirmRemoveTransporterOrder';
import MoveOrderToDifferentTripSelect from '@/modules/newOrder/modals/subOrderMenu/MoveOrderToDifferentTripSelect';
import moment from 'moment';

export default {
	name: 'SubOrderMenu',
	components: {
		TStatusColorDot,
		AddSubOrderBefore,
		ConfirmDeleteSubOrder,
		AddOrderToTripSelect,
		AddOrderToTransporterSelect,
		ConfirmRemoveTripOrder,
		ConfirmRemoveTransporterOrder,
		MoveOrderToDifferentTripSelect,
	},
	props: {
		subOrderTabs: Array
	},
	data() {
		return {
			processingSubOrder: null,
			subOrderTrip: null,
			mergeOrder: false,
			addSubOrderBeforeVisible: false,
			confirmDeleteSubOrderVisible: false,
			addToTripVisible: false,
			addToTripProcessing: false,
			addToTransporterVisible: false,
			addToTransporterProcessing: false,
			confirmRemoveTripOrderVisible: false,
			confirmRemoveTransporterOrderVisible: false,
			moveLoadedOrderVisible: false,
			moveLoadedOrderProcessing: false,
		};
	},
	computed: {
		...mapState([
			'subOrders',
			'errors',
			'currentOrderTabIndex',
		]),
	},
	methods: {
		...mapActions([
			'insertSubOrderBefore',
			'removeSubOrder',
			'addOrderToTrip',
			'addOrderToTransporter',
			'removeOrderFromTrip',
			'removeOrderFromTransporter',
			'moveOrderToDifferentTrip',
			'getSubOrderTrip'
		]),
		getStatusColor(status) {
			return statuses.color(status);
		},

		showAddSubOrderBefore(subOrderIndex) {
			this.processingSubOrder = this.subOrders[subOrderIndex];
			this.addSubOrderBeforeVisible = true;
		},
		async onAddSubOrderBefore(data) {
			try {
				await this.insertSubOrderBefore(data);

				this.$notify({
					type: 'success',
					title: this.$t('subOrderAdded')
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t(e.message || 'failedToAddSubOrderBefore'),
					duration: -1
				});
			}
		},
		onDeleteSubOrder(subOrderIndex) {
			this.processingSubOrder = this.subOrders[subOrderIndex];
			this.confirmDeleteSubOrderVisible = true;
		},
		async confirmDeleteSubOrder() {
			try {
				await this.removeSubOrder(this.processingSubOrder.id);

				this.$notify({
					type: 'success',
					title: this.$t('subOrderDeleted')
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t(e.message || 'failedToDeleteSubOrder'),
					duration: -1
				});
			}
		},
		showAddToTrip(subOrderIndex) {
			this.processingSubOrder = this.subOrders[subOrderIndex];
			this.addToTripVisible = true;
		},
		async onAddOrderToTrip(data) {
			this.addToTripProcessing = true;
			let tripNumber = data.trip ? data.trip.tripNumber : '';
			try {
				await this.addOrderToTrip(data);

				this.$notify({
					type: 'success',
					title: `${this.$tc('orderAddedToTrip', { number: tripNumber })}`
				});
				this.addToTripVisible = false;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$tc(e.message || 'failedToAddOrderToTrip', { number: tripNumber }),
					duration: -1
				});
			}
			this.addToTripProcessing = false;
		},
		showAddToTransporter(subOrderIndex) {
			this.processingSubOrder = this.subOrders[subOrderIndex];
			this.addToTransporterVisible = true;
		},
		async onAddOrderToTransporter(body) {
			this.addToTransporterProcessing = true;
			try {
				await this.addOrderToTransporter({ ...body, order: this.processingSubOrder });

				this.$notify({
					type: 'success',
					title: `${this.$t('orderAddedToTransporter')}`
				});
				this.addToTransporterVisible = false;

			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t(e.message || 'failedToAddOrderToTransporter'),
					duration: -1
				});
			}
			this.addToTransporterProcessing = false;
		},
		onRemoveOrder(subOrderIndex, merge = false) {
			this.processingSubOrder = this.subOrders[subOrderIndex];
			if (!this.processingSubOrder) return;
			this.mergeOrder = merge;

			if (this.processingSubOrder.external) {
				let timeCheck = moment.utc().subtract(5,'minutes')
					.valueOf();
				let hasSentOrders = this.selectedOrder.createdAt < timeCheck || this.selectedOrder.sentStatus;
				if (hasSentOrders)
					this.confirmRemoveTransporterOrderVisible = true;
				else
					this.confirmRemoveOrderFromTransporter();
			} else {
				this.confirmRemoveTripOrderVisible = true;
			}
		},
		confirmRemoveOrderFromTrip() {
			try {
				this.removeOrderFromTrip({ order: this.processingSubOrder, merge: this.mergeOrder });
				this.$notify({
					type: 'success',
					title: `${this.$t('orderRemovedFromTrip')}`
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToRemoveOrderTrip')
				});
			}
		},
		confirmRemoveOrderFromTransporter() {
			try {
				this.removeOrderFromTransporter({ orders: [this.selectedOrder], merge: this.mergeOrder });
				this.$notify({
					type: 'success',
					title: this.$t('orderRemovedFromTransporter')
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToRemoveOrderTransporter')
				});
			}
		},
		async showMoveLoadedOrder(subOrderIndex) {
			this.processingSubOrder = this.subOrders[subOrderIndex];
			this.subOrderTrip = await this.getSubOrderTrip(this.processingSubOrder.tripId);
			this.moveLoadedOrderVisible = true;
		},
		async onMoveOrderToDifferentTrip(data) {

			this.moveLoadedOrderProcessing = true;
			try {
				await this.moveOrderToDifferentTrip(data);

				this.$notify({
					type: 'success',
					title: `${this.$t('orderAddedToTrip', { number: data.newTrip.tripNumber })}`
				});
				this.moveLoadedOrderVisible = false;

			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t(e.message || 'failedToAddOrderToTrip', { number: data.newTrip.tripNumber }),
					duration: -1
				});
			}
			this.moveLoadedOrderProcessing = false;
		}
	}
};
</script>